import { AgGridReact } from 'ag-grid-react';
import { Theme, themeQuartz } from "ag-grid-community";
import React, { useEffect, useMemo } from 'react';
import Loader from '../../../../components/loader';
import { useParams } from 'react-router-dom';
import { useAssetsApi } from '../../../../store/api/hooks/assetsApiHook';
import { titleCell } from '../../../../components/elements/function';
import { useUserSelector } from '../../../../store/selectors/userSelector';
import Currency from '../../../../components/currency';
import { showLocalDate } from '../../../../_common/functions';


const PartsConsumed: React.FC = () => {
    const params: any = useParams();
    const user: any = useUserSelector();
    const [rowData, setRowData] = React.useState<any[] | null>([]);
    const assetsAPI = useAssetsApi();
    const myTheme = themeQuartz.withParams({
        headerBackgroundColor: `#F0F0F0`
    });
    const theme = useMemo<Theme | "legacy">(() => {
        return myTheme;
    }, []);

    const getUsedSpareParts = (assetID: number) => {
        const payload = {
            userID: user?.userID || 0,
            assetID: assetID,
            pageSize: 0,
            pageNumber: 0
        };

        assetsAPI.GetWorkOrderUsedSpareParts(
            payload,
            (resp: any) => {
                setRowData(resp.assetSparePartsUsedList || []);
            },
            (error: any) => {
                console.error("Error fetching used spare parts:", error);
                setRowData([]);
            }
        );
    };

    useEffect(() => {
        if (params.id) {
            getUsedSpareParts(params.id);
        }
    }, [params.id]);

    const columnDefs = [
        {
            headerName: 'WO Number',
            field: 'workOrderCode',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 120,
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value || '--'}>
                    {titleCell(data)}
                </p>
            ),
        },
        {
            headerName: 'Part Code',
            field: 'partsCode',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 140,
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value || '--'}>
                    {titleCell(data)}
                </p>
            ),
        },
        {
            headerName: 'Part Name',
            field: 'sparePartsName',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 150,
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value || '--'}>
                    {titleCell(data)}
                </p>
            ),
        },
        {
            headerName: 'Facility',
            field: 'facilityName',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 150,
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value || '--'}>
                    {titleCell(data)}
                </p>
            ),
        },
        {
            headerName: 'Consumed Quantity',
            field: 'usedQty',
            headerClass: 'bg-[#f0f0f0] ag-right-aligned-header',
            flex: 1,
            minWidth: 140,
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value ? data.value.toString() : '0'}>
                    {data.value || 0}
                </p>
            ),
        },
        {
            headerName: 'Cost per Unit',
            field: 'pricePerUnit',
            headerClass: 'bg-[#f0f0f0] ag-right-aligned-header',
            flex: 1,
            minWidth: 120,
            cellClass: 'ag-right-aligned-cell',
            cellRenderer: (data: any) => (
                <Currency currency="USD" value={data.value || 0} />
            ),
        },
        {
            headerName: 'Total Cost',
            field: 'totalCost',
            headerClass: 'bg-[#f0f0f0] ag-right-aligned-header',
            flex: 1,
            minWidth: 120,
            cellClass: 'ag-right-aligned-cell',
            valueGetter: (params: any) => {
                const usedQty = params.data.usedQty || 0;
                const pricePerUnit = params.data.pricePerUnit || 0;
                return usedQty * pricePerUnit;
            },
            cellRenderer: (data: any) => (
                <Currency currency="USD" value={data.value || 0} />
            ),
        },
        {
            headerName: 'Start Date',
            field: 'originalStartDate',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 120,
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value ? showLocalDate(data.value) : '--'}>
                    {data.value ? showLocalDate(data.value) : '--'}
                </p>
            ),
        },
        {
            headerName: 'End Date',
            field: 'originalEndDate',
            headerClass: 'bg-[#f0f0f0]',
            flex: 1,
            minWidth: 120,
            cellRenderer: (data: any) => (
                <p className="truncate" title={data.value ? showLocalDate(data.value) : '--'}>
                    {data.value ? showLocalDate(data.value) : '--'}
                </p>
            ),
        },
    ];

    return (
        <div>
            <div className="flex items-center justify-between h-[60px]">
                <h4 className="font-semibold text-[0.938rem]">Parts and Supplies Consumed</h4>
            </div>
            <div className={`mb-3 ag-theme-alpine overflow-auto ${!rowData || rowData.length === 0 || rowData.length <= 2 ? 'h-[150px]' : 'h-[300px]'}`}>
                <AgGridReact
                    theme={theme}
                    columnDefs={columnDefs}
                    rowData={rowData || []}
                    pagination={false}
                    domLayout="normal"
                    ensureDomOrder={true}
                    suppressHorizontalScroll={false}
                    loadingOverlayComponent={Loader}
                    noRowsOverlayComponent={() => (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500">
                                <div className="text-lg font-medium">No Data Found</div>
                            </div>
                        </div>
                    )}
                />
            </div>
        </div>
    );
};

export default PartsConsumed;